import React from 'react'
import { CandidateConnectionLiteDto } from '../api/role/models/CandidateConnectionDto'
import PersonUi from './PersonUi'
import { useManageRoleStore } from './manageRole/stateStore/manageRoleStore'
import { formatDuration } from '../utils/dateUtils'
import PersonSmallPic from './PersonSmallPic'
import InterviewForThisRolePill from './InterviewForThisRolePill'

interface Props {
    connection: CandidateConnectionLiteDto
}

function Connection({ connection }: Props): React.JSX.Element {
    const companyCache = useManageRoleStore().getState().companyCache
    const selectedCandidateId = useManageRoleStore(
        (state) => state.selectedCandidateId,
    )
    const personLiteCache = useManageRoleStore().getState().personLiteCache

    const groupedByCompany = connection.sharedWorkHistory.reduce(
        (acc, item) => {
            if (!acc[item.companyId]) acc[item.companyId] = []
            acc[item.companyId].push(item)
            return acc
        },
        {} as Record<string, typeof connection.sharedWorkHistory>,
    )

    const totalWorkedTogether = formatDuration(
        connection.sharedWorkHistory[connection.sharedWorkHistory.length - 1]
            ?.startOfOverlap,
        connection.sharedWorkHistory[0]?.endOfOverlap,
    ).duration

    const connectedPerson = personLiteCache.getOrThrow(
        connection.connectedPersonId,
    )

    return (
        <div className="p-4 mb-4">
            <div className="flex flex-col mb-4">
                <div className="flex gap-4">
                    <PersonUi
                        person={connectedPerson}
                        showCurrentJobDetails={false}
                        imageClasses="w-[60px] h-[60px]"
                    />
                    <div className='ml-auto mr-8 mt-auto'>
                        <div className="field-label">Interview</div>
                        <InterviewForThisRolePill
                            value={connection.interviewForThisRole}
                        />
                    </div>
                </div>
                <div className="text-gray-500 text-sm mt-1">
                    <span className="font-medium">{totalWorkedTogether}</span>
                </div>
            </div>

            {Object.entries(groupedByCompany).map(([companyId, items]) => {
                const company = companyCache.getOrThrow(companyId)

                const totalCompanyDuration = formatDuration(
                    items[items.length - 1]?.startOfOverlap,
                    items[0]?.endOfOverlap,
                ).duration

                const candidate = personLiteCache.getOrThrow(
                    selectedCandidateId!,
                )

                return (
                    <div
                        key={companyId}
                        className="border border-gray-300 rounded-lg pb-2 mb-4 last:mb-0"
                    >
                        <div className="p-2 logo-container flex gap-2 items-center rounded-t-lg bg-gray-100">
                            <img
                                src={company?.logoUrl ?? '/no-company-logo.png'}
                                alt={company.name}
                                className="w-12 h-12 object-cover border"
                            />
                            <div>
                                <div className="font-semibold">
                                    {company.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                    <span className="font-medium">
                                        {totalCompanyDuration}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center mt-4">
                            <div className="w-1/3 flex justify-center">
                                <PersonSmallPic
                                    person={connectedPerson}
                                    size="40px"
                                />
                            </div>

                            <div className="w-[10px] flex justify-center"></div>

                            <div className="w-1/3 flex justify-center">
                                <PersonSmallPic
                                    person={candidate}
                                    size="40px"
                                />
                            </div>
                        </div>

                        {items.map((item, index) => {
                            const roleDuration = formatDuration(
                                item.startOfOverlap,
                                item.endOfOverlap,
                            )

                            return (
                                <div
                                    key={index}
                                    className="flex items-center"
                                >
                                    <div className="w-1/3 text-center pl-2 pr-1 text-xs line-clamp-3 overflow-scroll">
                                        {item.seedPersonWorkedAt.jobTitle}
                                    </div>

                                    <div className="w-[10px] flex justify-center">
                                        <div className="relative h-full min-h-[50px] flex items-center justify-center">
                                            <div className="absolute top-0 bottom-0 w-[2px] bg-gray-300" />
                                            <div className="w-2 h-2 bg-gray-300 rounded-full z-10" />
                                        </div>
                                    </div>

                                    <div className="w-1/3 text-center pl-1 text-xs">
                                        {item.resolvedPersonWorkedAt.jobTitle}
                                    </div>

                                    <div className="flex-1 text-center text-xs text-gray-500">
                                        <div className="text-nowrap">
                                            {roleDuration.fromTo}
                                        </div>
                                        <div className="flex-1 text-center whitespace-nowrap">
                                            {roleDuration.duration}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )
            })}
        </div>
    )
}

export default Connection
